// @flow
import * as React from 'react'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Modal from 'ui/atoms/Modal'
import SemiBoldText from 'ui/atoms/SemiBoldText'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import SignatureQuestionRender from 'ui/SignatureQuestionRender'
import { ZentakeContext } from 'utils/ZentakeContext'

import type { UserDetail } from '@/types/user'

type Props = {
  userDetails: ?UserDetail | null,
  setUserDetails: (UserDetail | ((UserDetail) => UserDetail)) => void,
}

function ProfileTabSignatureFields({
  userDetails,
  setUserDetails,
}: Props): React.Node {
  const { teamConfig } = React.useContext(ZentakeContext)

  const [signatureModalOpen, setSignatureModalOpen] = React.useState(false)
  const [initialsModalOpen, setInitialsModalOpen] = React.useState(false)

  const toggleSignatureModal = React.useCallback(
    () => setSignatureModalOpen(!signatureModalOpen),
    [signatureModalOpen]
  )
  const toggleInitialsModal = React.useCallback(
    () => setInitialsModalOpen(!initialsModalOpen),
    [initialsModalOpen]
  )

  const updateProviderSignature = React.useCallback(
    (answer) => {
      setUserDetails((userDetails) =>
        userDetails
          ? { ...userDetails, provider_signature: answer.image }
          : userDetails
      )
      toggleSignatureModal()
    },
    [setUserDetails, toggleSignatureModal]
  )

  const updateProviderInitials = React.useCallback(
    (answer) => {
      setUserDetails((userDetails) =>
        userDetails
          ? { ...userDetails, provider_initials: answer.image }
          : userDetails
      )
      toggleInitialsModal()
    },
    [setUserDetails, toggleInitialsModal]
  )

  const clearProviderSignature = React.useCallback(() => {
    setUserDetails((userDetails) =>
      userDetails ? { ...userDetails, provider_signature: null } : userDetails
    )
  }, [setUserDetails])

  const clearProviderInitials = React.useCallback(() => {
    setUserDetails((userDetails) =>
      userDetails ? { ...userDetails, provider_initials: null } : userDetails
    )
  }, [setUserDetails])

  if (!teamConfig?.enable_provider_signature) return null

  return (
    <>
      <Space>
        <Space size="small">
          <SemiBoldText>Electronic Signature</SemiBoldText>
          <Text $small>
            I hereby give Zentake permission to electronically replicate my
            hand-drawn signature, which I provide for electronic signing within
            the Zentake, on printed, faxed, or electronically transmitted
            documents.
          </Text>
        </Space>
        <Flex $horizontalSpacing="large">
          {!userDetails?.provider_signature && (
            <Button onClick={toggleSignatureModal}>Add Signature</Button>
          )}
          {!userDetails?.provider_initials && (
            <Button onClick={toggleInitialsModal}>Add Initials</Button>
          )}
        </Flex>
        <Flex $horizontalSpacing="large">
          {userDetails?.provider_signature && (
            <FlexBox flex={3}>
              <SignatureQuestionRender
                forceClearButton
                hideConfirmButton
                hideConsentText
                hideLabel
                readonly
                smallSignature
                answer={{ image: userDetails?.provider_signature, text: null }}
                onConfirm={async () => undefined}
                onReset={clearProviderSignature}
              />
            </FlexBox>
          )}
          {userDetails?.provider_initials && (
            <FlexBox flex={1}>
              <SignatureQuestionRender
                forceClearButton
                hideConfirmButton
                hideConsentText
                hideInitialAll
                hideLabel
                initials
                readonly
                smallSignature
                answer={{ image: userDetails?.provider_initials, text: null }}
                onConfirm={async () => undefined}
                onReset={clearProviderInitials}
              />
            </FlexBox>
          )}
        </Flex>
      </Space>
      <Modal
        closeButton
        onHide={toggleSignatureModal}
        show={signatureModalOpen}
      >
        <SignatureQuestionRender
          answer={{ image: null, text: null }}
          onConfirm={updateProviderSignature}
          onReset={() => undefined}
          subtype="draw"
        />
      </Modal>
      <Modal closeButton onHide={toggleInitialsModal} show={initialsModalOpen}>
        <SignatureQuestionRender
          hideInitialAll
          initials
          answer={{ image: null, text: null }}
          onConfirm={updateProviderInitials}
          onReset={() => undefined}
          subtype="draw"
        />
      </Modal>
    </>
  )
}

export default ProfileTabSignatureFields
